import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { CreateCollection } from "@/shared/components/collections/CreateCollection";
import { Button } from "@/shared/foundation/primitives/Button";

import styles from "./CreateCollectionButton.module.css";

interface CreateCollectionButtonProps {
  label?: string;
  variant?: "contained" | "outlined" | "filter";
  menuOrigin?: "left" | "center" | "right";
}

export function CreateCollectionButton({
  label = "New",
  variant = "contained",
  menuOrigin = "right",
}: CreateCollectionButtonProps) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, handleClose]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    // CreateCollection's Name field already has autoFocus, so this is usually
    // a no-op — but if that ever changes, fall back to focusing the panel
    // itself so keyboard users aren't left stranded on the trigger button.
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }
  }, [open]);

  const muiVariant = variant === "filter" ? "outlined" : variant;

  const triggerClassMap: Record<typeof variant, string | undefined> = {
    contained: styles.trigger,
    filter: styles.filterTrigger,
    outlined: undefined,
  };
  const triggerClass = triggerClassMap[variant];

  const panelClassMap: Record<typeof menuOrigin, string> = {
    left: styles.panelLeft,
    center: styles.panelCenter,
    right: styles.panelRight,
  };
  const panelClass = panelClassMap[menuOrigin];

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <Button
        ref={triggerRef}
        variant={muiVariant}
        color={
          muiVariant === "outlined" && variant !== "filter"
            ? "primary"
            : undefined
        }
        size="small"
        startIcon={
          <PlusIcon className={styles.triggerIcon} aria-hidden="true" />
        }
        className={triggerClass}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
      </Button>
      {open && (
        <div
          ref={panelRef}
          className={`${styles.panel} ${panelClass}`}
          role="dialog"
          aria-label="Create collection"
          tabIndex={-1}
        >
          <CreateCollection close={handleClose} />
        </div>
      )}
    </div>
  );
}
